import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

export interface BadgeProps {
  readonly children: ReactNode;
  readonly tone?: 'brand' | 'neutral' | 'beta' | 'beta-pill';
  readonly className?: string;
}

/**
 * `TONE` mang TRỌN class của mỗi tone, không phải chỉ một cặp màu.
 *
 * Vì sao không dùng "base class chung + cặp màu theo tone": thiết kế vẽ badge `BETA` là **chữ
 * trần** — không nền, không pill, không padding. Nó khác các tone khác ở HÌNH DẠNG chứ không chỉ
 * ở màu, nên không có base class nào áp được cho cả bốn.
 *
 * Số đo, lấy nguyên từ `template.html`:
 *
 * | tone        | ở đâu                    | thiết kế ghi |
 * | ----------- | ------------------------ | ------------ |
 * | `beta`      | `#modules`, 2 lượt       | 9.5px · 800 · violet-fg · `.06em` · KHÔNG nền |
 * | `beta-pill` | `#knowledge`, 1 lượt     | như trên + nền violet-bg + bo tròn + padding |
 * | `brand`     | badge/chip nói chung     | cặp `brand-50`/`brand-600` — đo 7.27:1, đạt AA |
 * | `neutral`   | badge/chip nói chung     | cặp `ink-100`/`ink-700` |
 *
 * Hai chỗ lệch thiết kế có chủ ý, đã hỏi user (2026-08-19):
 *   1. `beta-pill` ở thiết kế dùng `letter-spacing:.08em`, ở đây dùng `tracking-label` (.06em) —
 *      `theme.css` không có token .08em, và ở cỡ 9.5px thì chênh lệch là ~0.19px. Không thêm
 *      token cho đúng một chỗ dùng.
 *   2. Thiết kế ghi padding dọc 3px; thang khoảng của Tailwind không có bậc đó nên dùng bậc 2px.
 *
 * `--text-nano` được dùng ở đây dù luật 7 của skill nói "chỉ dùng trong mock giao diện sản phẩm":
 * user đã nới luật đó cho badge (2026-08-19), vì dùng cỡ khác là lệch thiết kế thấy được bằng mắt.
 * File skill do lô A′ làm chủ nên việc sửa câu chữ của luật thuộc về họ, không phải lô 0.
 */
const TONE = {
  brand:
    'inline-flex items-center rounded-pill px-3 py-1 text-micro font-semibold bg-brand-50 text-brand-600',
  neutral:
    'inline-flex items-center rounded-pill px-3 py-1 text-micro font-semibold bg-ink-100 text-ink-700',
  beta: 'align-middle text-nano font-extrabold tracking-label text-violet-fg',
  'beta-pill':
    'inline-flex items-center rounded-pill px-2 py-0.5 text-nano font-extrabold tracking-label bg-violet-bg text-violet-fg',
} as const;

export function Badge({ children, tone = 'brand', className }: BadgeProps) {
  return <span className={cn(TONE[tone], className)}>{children}</span>;
}
